/* eslint-disable camelcase */
import React, { useState, useMemo, useCallback, useEffect, ElementType } from 'react';
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Badge,
  Text,
  HStack,
  Icon,
  Grid,
  GridItem,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import InfiniteScroll from 'react-infinite-scroller';
import { useGetIncludedOrdersQuery, Order, Order_By, useGetMyRestaurantQuery } from '../utils/__generated__/graphql';
import { OrderCard, OrderState, PageHeader, ModernCard, LoadingSpinner } from './shared';
import { useUserDefaultRole, useUserId } from '@nhost/react';
import { FiShoppingBag, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { motion } from 'framer-motion';

dayjs.extend(relativeTime);

const MotionBox = motion(Box);

const COMPLETED_STATES = [OrderState.COMPLETED];
const CANCELED_STATE = OrderState.CANCELED;

const OrderHistory: React.FC = () => {
  const [activeTab, setActiveTab] = useState(() => Number(localStorage.getItem('orderHistoryActiveTab') || '0'));

  const userRole = useUserDefaultRole();

  const [hasMore, setHasMore] = useState({
    COMPLETED: true,
    CANCELED: true,
  });

  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('brand.500', 'brand.300');

  const userId = useUserId();
  const { data: restaurantData, loading: loadingRestaurant } = useGetMyRestaurantQuery({
    variables: { id: userId },
    skip: !userId,
    fetchPolicy: 'cache-first',
  });

  const restaurantId = restaurantData?.restaurant?.[0]?.id;

  // Only execute queries when restaurantId is available and not loading
  const shouldExecuteQueries = Boolean(restaurantId) && !loadingRestaurant;



  // Use conditional rendering to prevent query execution
  const completedOrdersQuery = shouldExecuteQueries ? useGetIncludedOrdersQuery({
    variables: {
      includedStates: COMPLETED_STATES,
      _is_null: false,
      order_by: [{ created_at: Order_By.Desc }],
      limit: 30,
      offset: 0,
      restaurantId: restaurantId!,
    },
  }) : { data: undefined, fetchMore: () => { }, loading: false };

  const canceledOrdersQuery = shouldExecuteQueries ? useGetIncludedOrdersQuery({
    variables: {
      includedStates: [CANCELED_STATE],
      _is_null: true,
      order_by: [{ created_at: Order_By.Desc }],
      limit: 30,
      offset: 0,
      restaurantId: restaurantId!,
    },
  }) : { data: undefined, fetchMore: () => { }, loading: false };

  const {
    data: completedOrders,
    fetchMore: fetchMoreCompletedOrders,
    loading: loadingCompleted,
  } = completedOrdersQuery;

  const {
    data: canceledOrders,
    fetchMore: fetchMoreCanceledOrders,
    loading: loadingCanceled,
  } = canceledOrdersQuery;

  const loadMore = useCallback(
    (fetchMore: any, orders: Order[] | undefined, type: OrderState.COMPLETED | OrderState.CANCELED) => {
      fetchMore({
        variables: { offset: orders?.length ?? 0 },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult?.order.length) {
            setHasMore((prev) => ({ ...prev, [type]: false }));
            return prev;
          }

          // Filter out duplicates by creating a Set of IDs
          const existingIds = new Set((prev.order || []).map((o: Order) => o.id));
          const newOrders = fetchMoreResult.order.filter((o: Order) => !existingIds.has(o.id));

          return {
            ...prev,
            order: [...(prev.order || []), ...newOrders],
          };
        },
      });
    },
    []
  );

  useEffect(() => {
    localStorage.setItem('orderHistoryActiveTab', activeTab.toString());
  }, [activeTab]);

  const filteredCompletedOrders = useMemo(
    () =>
      COMPLETED_STATES.reduce(
        (acc, status) => ({
          ...acc,
          [status]: completedOrders?.order.filter((order) => order?.status === status) ?? [],
        }),
        {} as Record<OrderState, Order[]>
      ),
    [completedOrders?.order]
  );

  const renderOrderList = useCallback(
    (orders: Order[], type: OrderState.COMPLETED | OrderState.CANCELED) => (
      <InfiniteScroll
        pageStart={0}
        loadMore={() =>
          loadMore(
            type === OrderState.COMPLETED ? fetchMoreCompletedOrders : fetchMoreCanceledOrders,
            type === OrderState.COMPLETED ? (completedOrders?.order as Order[]) : (canceledOrders?.order as Order[]),
            type
          )
        }
        hasMore={hasMore[type]}
        loader={
          <Box key="loader" textAlign="center" py={4}>
            <LoadingSpinner size="sm" text="Loading more orders..." />
          </Box>
        }
      >
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
          {orders.map((order: Order) => (
            <GridItem key={order.id}>
              <OrderCard order={order} userRole={userRole as any} />
            </GridItem>
          ))}
        </Grid>
      </InfiniteScroll>
    ),
    [
      completedOrders?.order,
      canceledOrders?.order,
      fetchMoreCompletedOrders,
      fetchMoreCanceledOrders,
      hasMore,
      loadMore,
      userRole,
    ]
  );

  // Calculate order counts
  const completedCount = completedOrders?.order?.length || 0;
  const canceledCount = canceledOrders?.order?.length || 0;
  const totalCount = completedCount + canceledCount;

  // Add a useMemo to deduplicate the canceled orders before rendering
  const uniqueCanceledOrders = useMemo(() => {
    if (!canceledOrders?.order) return [];

    // Use a Map to keep only the latest version of each order by ID
    const orderMap = new Map();
    canceledOrders.order.forEach((order) => {
      orderMap.set(order.id, order);
    });

    return Array.from(orderMap.values());
  }, [canceledOrders?.order]);

  return (
    <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <PageHeader
        title="Order History"
        subtitle="View and manage all order history"
        badge={
          <Badge colorScheme="brand" fontSize="sm" borderRadius="full" px={3} py={1}>
            {totalCount} Orders
          </Badge>
        }
      />

      {/* Order Summary */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 6 }} mb={{ base: 6, md: 8 }}>
        <ModernCard p={{ base: 4, md: 6 }} hover>
          <Stat>
            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
              Total Orders
            </StatLabel>
            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={accentColor}>
              {totalCount}
            </StatNumber>
            <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
              <HStack spacing={2}>
                <Icon as={FiShoppingBag as ElementType} boxSize={{ base: 3, md: 4 }} />
                <Text>All orders</Text>
              </HStack>
            </StatHelpText>
          </Stat>
        </ModernCard>

        <ModernCard p={{ base: 4, md: 6 }} hover>
          <Stat>
            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
              Completed Orders
            </StatLabel>
            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="green.500">
              {completedCount}
            </StatNumber>
            <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
              <HStack spacing={2}>
                <Icon as={FiCheckCircle as ElementType} boxSize={{ base: 3, md: 4 }} />
                <Text>Successfully delivered</Text>
              </HStack>
            </StatHelpText>
          </Stat>
        </ModernCard>

        <ModernCard p={{ base: 4, md: 6 }} hover>
          <Stat>
            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
              Canceled Orders
            </StatLabel>
            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="red.500">
              {canceledCount}
            </StatNumber>
            <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
              <HStack spacing={2}>
                <Icon as={FiXCircle as ElementType} boxSize={{ base: 3, md: 4 }} />
                <Text>Cancelled by user</Text>
              </HStack>
            </StatHelpText>
          </Stat>
        </ModernCard>
      </SimpleGrid>

      {/* Loading State */}
      {(loadingRestaurant || loadingCompleted || loadingCanceled) && !completedOrders?.order && !canceledOrders?.order && (
        <LoadingSpinner text={loadingRestaurant ? "Loading restaurant data..." : "Loading order history..."} />
      )}

      {/* Tabs */}
      {!shouldExecuteQueries && !loadingRestaurant && (
        <Box textAlign="center" py={8}>
          <Text color={textColor}>No restaurant found. Please check your account settings.</Text>
        </Box>
      )}

      {shouldExecuteQueries && (
        <Tabs
          size={{ base: 'sm', md: 'md' }}
          variant="soft-rounded"
          colorScheme="brand"
          index={activeTab}
          onChange={setActiveTab}
          isLazy
        >
          <Box
            borderRadius="xl"
            overflow="hidden"
            backgroundColor={useColorModeValue('white', 'gray.800')}
            shadow="sm"
            pt={6}
            px={4}
            alignItems="center"
            display="flex"
          >
            <TabList mb={{ base: 4, md: 6 }}>
              {COMPLETED_STATES.map((status) => (
                <Tab
                  key={status}
                  _selected={{ color: 'white', bg: 'brand.500' }}
                  fontWeight="medium"
                  fontSize={{ base: 'sm', md: 'md' }}
                >
                  <HStack spacing={2}>
                    <Icon as={FiCheckCircle as ElementType} boxSize={{ base: 4, md: 5 }} />
                    <Text>{status}</Text>
                    <Badge colorScheme="green" borderRadius="full" fontSize="xs">
                      {filteredCompletedOrders[status]?.length || 0}
                    </Badge>
                  </HStack>
                </Tab>
              ))}
              <Tab
                _selected={{ color: 'white', bg: 'brand.500' }}
                fontWeight="medium"
                fontSize={{ base: 'sm', md: 'md' }}
              >
                <HStack spacing={2}>
                  <Icon as={FiXCircle as ElementType} boxSize={{ base: 4, md: 5 }} />
                  <Text>{CANCELED_STATE}</Text>
                  <Badge colorScheme="red" borderRadius="full" fontSize="xs">
                    {canceledOrders?.order?.length || 0}
                  </Badge>
                </HStack>
              </Tab>
            </TabList>
          </Box>
          <TabPanels>
            {COMPLETED_STATES.map((status) => (
              <TabPanel key={status} px={0}>
                {renderOrderList(filteredCompletedOrders[status], OrderState.COMPLETED)}
              </TabPanel>
            ))}
            <TabPanel px={0}>{renderOrderList(uniqueCanceledOrders, OrderState.CANCELED)}</TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </MotionBox>
  );
};

export default OrderHistory;
